<template>
  <div>
    <div class="header bg-gradient-success py-7 py-lg-8 pt-lg-9">
      <div class="separator separator-bottom separator-skew zindex-100">
        <svg x="0" y="0" viewBox="0 0 2560 100" preserveAspectRatio="none" version="1.1"
          xmlns="http://www.w3.org/2000/svg">
          <polygon class="fill-default" points="2560 0 2560 100 0 100"></polygon>
        </svg>
      </div>
    </div>

    <b-container class="mt--8 pb-5">
      <b-row class="justify-content-center">
        <b-col lg="5" md="7">
          <b-card no-body class="bg-secondary mb-0">
            <b-card-body class="px-lg-5 py-lg-5">

              <!-- Logo -->
              <div class="text-center mb-4">
                <img src="/img/brand/inexlink.png" alt="InexLink" style="max-width:160px;" />
              </div>

              <!-- Loading state -->
              <div v-if="cargando" class="text-center py-4">
                <b-spinner variant="primary" />
              </div>

              <!-- Not found -->
              <div v-else-if="noEncontrado" class="text-center py-4">
                <i class="fas fa-exclamation-circle text-danger fa-3x mb-3"></i>
                <p class="text-muted">El enlace no es válido o ha expirado.</p>
              </div>

              <!-- Already registered -->
              <div v-else-if="yaRegistrado" class="text-center py-4">
                <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
                <h4 class="text-success">Ya tienes una cuenta</h4>
                <p class="text-muted">Ya existe una cuenta registrada en InexLink con tu correo. Ingresa con tus credenciales para ver tu cotización.</p>
                <b-button variant="primary" @click="$router.push('/auth/login')">Iniciar sesión</b-button>
              </div>

              <!-- Success -->
              <div v-else-if="exito" class="text-center py-4">
                <i class="fas fa-check-circle fa-3x mb-3" style="color:#00b8d4;"></i>
                <h4 style="color:#1e3a5f; font-weight:700;">¡Cuenta creada exitosamente!</h4>
                <p class="text-muted">Tu cuenta ha sido creada. Ya puedes iniciar sesión en la plataforma.</p>
                <b-button variant="primary" @click="$router.push('/auth/login')">Iniciar sesión</b-button>
              </div>

              <!-- Registration form -->
              <template v-else-if="datos">
                <h4 class="text-center mb-1" style="color:#1e3a5f;">Crea tu cuenta</h4>
                <p class="text-center text-muted mb-4" style="font-size:0.87rem;">
                  Completa tu registro para acceder a la plataforma InexLink.
                </p>

                <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">{{ error }}</b-alert>

                <b-form @submit.prevent="submitForm">
                  <b-form-group label="Nombre" label-class="form-label">
                    <b-form-input :value="datos.nombre" disabled class="bg-light" />
                  </b-form-group>

                  <b-form-group label="Correo electrónico" label-class="form-label">
                    <b-form-input :value="datos.email" disabled class="bg-light" />
                  </b-form-group>

                  <b-form-group label="Contraseña *" label-class="form-label">
                    <div class="input-group">
                      <b-form-input
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="Mínimo 5 caracteres"
                        required
                        autocomplete="new-password"
                        :state="passwordState"
                        class="border rounded-left rounded-right-none"
                      />
                      <div class="input-group-append">
                        <button type="button" class="btn btn-outline-secondary border rounded-right rounded-left-none" @click="showPassword = !showPassword">
                          <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'" />
                        </button>
                      </div>
                    </div>
                    <b-form-invalid-feedback :state="passwordState">
                      La contraseña debe tener al menos 5 caracteres.
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <b-form-group label="Confirmar contraseña *" label-class="form-label">
                    <b-form-input
                      v-model="form.confirmPassword"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="Repite tu contraseña"
                      required
                      autocomplete="new-password"
                      :state="confirmState"
                    />
                    <b-form-invalid-feedback :state="confirmState">
                      Las contraseñas no coinciden.
                    </b-form-invalid-feedback>
                  </b-form-group>

                  <b-button type="submit" variant="primary" block :disabled="enviando">
                    <b-spinner small v-if="enviando" class="mr-1" />
                    {{ enviando ? 'Creando cuenta…' : 'Crear cuenta' }}
                  </b-button>
                </b-form>
              </template>

            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import peticionProductoService from '@/api/peticionProductoService';

export default {
  name: 'RegistroCompradorView',

  data() {
    return {
      cargando: true,
      noEncontrado: false,
      yaRegistrado: false,
      exito: false,
      datos: null,
      error: '',
      enviando: false,
      showPassword: false,
      form: {
        password: '',
        confirmPassword: '',
      },
    };
  },

  computed: {
    passwordState() {
      if (!this.form.password) return null;
      return this.form.password.length >= 5;
    },
    confirmState() {
      if (!this.form.confirmPassword) return null;
      return this.form.password === this.form.confirmPassword;
    },
  },

  async created() {
    const codigo = this.$route.params.codigo;
    try {
      const data = await peticionProductoService.getRegistroData(codigo);
      if (data.yaRegistrado) {
        this.yaRegistrado = true;
      } else {
        this.datos = data;
      }
    } catch (e) {
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
  },

  methods: {
    async submitForm() {
      if (!this.passwordState) {
        this.error = 'La contraseña debe tener al menos 5 caracteres.';
        return;
      }
      if (!this.confirmState) {
        this.error = 'Las contraseñas no coinciden.';
        return;
      }
      this.error = '';
      this.enviando = true;
      try {
        await peticionProductoService.crearCuentaComprador(this.$route.params.codigo, {
          password: this.form.password,
          confirmPassword: this.form.confirmPassword,
        });
        this.exito = true;
      } catch (e) {
        const resp = e && e.response && e.response.data;
        const msg = (resp && resp.message) || (typeof resp === 'string' ? resp : null) || 'Error al crear la cuenta. Intenta de nuevo.';
        this.error = typeof msg === 'string' ? msg : JSON.stringify(msg);
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #525f7f;
  margin-bottom: 4px;
}
</style>
